import { ref, computed } from 'vue';

export function useBulkInput(manualInputRequirements, levelFrameworkRequirements) {
  const self = {
    bulkRows: ref([]),
  };

  self.addBulkRow = () => {
    const newRow = {
      id: Date.now() + Math.random(),
      manualInputs: {},
      hierarchySelections: {},
      trackSelections: {}
    };

    manualInputRequirements.value.forEach(input => {
      newRow.manualInputs[input.property] = '';
    });

    levelFrameworkRequirements.value.forEach(req => {
      if (req.isTrackOnly) {
        newRow.trackSelections[req.ht_id] = null;
      } else {
        newRow.hierarchySelections[req.ht_id] = [];
        if (req.needsTrackSelection) {
          newRow.trackSelections[req.ht_id] = null;
        }
      }
    });

    self.bulkRows.value.push(newRow);
  };

  self.removeBulkRow = (rowId) => {
    const index = self.bulkRows.value.findIndex(r => r.id === rowId);
    if (index > -1) {
      self.bulkRows.value.splice(index, 1);
    }

    if (self.bulkRows.value.length === 0) {
      self.addBulkRow();
    }
  };

  function cartesianProduct(arrays) {
    if (arrays.length === 0) return [[]];
    if (arrays.some(arr => arr.length === 0)) return [[]];

    return arrays.reduce((acc, array) => {
      return acc.flatMap(x => array.map(y => [...(Array.isArray(x) ? x : [x]), y]));
    }, [[]]).filter(combo => combo.length === arrays.length);
  }

  self.expandRowsToExecutions = (rows) => {
    const expandedRows = [];

    rows.forEach((row) => {
      const hierarchyReqs = levelFrameworkRequirements.value.filter(req => !req.isTrackOnly);
      const htIds = hierarchyReqs.map(req => req.ht_id);

      const hierarchyArrays = htIds.map(htId => {
        const selections = row.hierarchySelections[htId] || [];
        return Array.isArray(selections) ? selections : [selections];
      });

      const combinations = cartesianProduct(hierarchyArrays);

      combinations.forEach(combination => {
        const hierarchySelectionsForRow = {};
        htIds.forEach((htId, index) => {
          hierarchySelectionsForRow[htId] = combination[index];
        });

        expandedRows.push({
          id: `${row.id}_${Date.now()}_${Math.random()}`,
          manualInputs: { ...row.manualInputs },
          hierarchySelections: hierarchySelectionsForRow,
          trackSelections: { ...(row.trackSelections || {}) },
          originalRowId: row.id
        });
      });
    });

    return expandedRows;
  };

  self.canExecuteBulk = computed(() => {
    if (self.bulkRows.value.length === 0) return false;

    return self.bulkRows.value.every(row => {
      const manualInputsFilled = manualInputRequirements.value.every(input => {
        return row.manualInputs[input.property] && row.manualInputs[input.property].trim() !== '';
      });

      const hierarchySelectionsMade = levelFrameworkRequirements.value
        .filter(req => !req.isTrackOnly)
        .every(req => {
          return Array.isArray(row.hierarchySelections[req.ht_id]) &&
                 row.hierarchySelections[req.ht_id].length > 0;
        });

      const trackSelectionsMade = levelFrameworkRequirements.value
        .filter(req => req.needsTrackSelection || req.isTrackOnly)
        .every(req => {
          return row.trackSelections?.[req.ht_id] != null && row.trackSelections[req.ht_id] !== '';
        });

      return manualInputsFilled && hierarchySelectionsMade && trackSelectionsMade;
    });
  });

  self.totalExecutionCount = computed(() => {
    if (self.bulkRows.value.length === 0) return 0;

    return self.bulkRows.value.reduce((total, row) => {
      const htIds = levelFrameworkRequirements.value.filter(req => !req.isTrackOnly).map(req => req.ht_id);

      let rowCombinations = 1;
      htIds.forEach(htId => {
        const selections = row.hierarchySelections[htId] || [];
        const count = Array.isArray(selections) ? selections.length : (selections ? 1 : 0);
        if (count > 0) {
          rowCombinations *= count;
        }
      });

      return total + rowCombinations;
    }, 0);
  });

  return self;
}
